<template>
  <component :is="buttonComponent" ref="button" :label="label" :submitting-label="submittingLabel"
             :form-valid="formValid" :submit-in-progress="submitInProgress" v-bind="extraButtonProps"
             v-on="buttonListener" @error="errorGettingToken" />
  <toast v-if="showError" kind="error">
    <template #message-content>
      <p>
        {{ i18n.t('public.bot_protection.error_getting_token') }}
      </p>
    </template>
  </toast>
</template>

<script>
import FriendlyCaptchaSubmitButton from '@/components/buttons/friendly-captcha-submit-button'
import HcaptchaSubmitButton from '@/components/buttons/hcaptcha-submit-button'
import RecaptchaSubmitButton from '@/components/buttons/recaptcha-submit-button'
import SubmitButton from '@/components/buttons/submit-button'
import Toast from '@/components/toast'
import TurnstileSubmitButton from '@/components/buttons/turnstile-submit-button.vue'

export default {
  components: { FriendlyCaptchaSubmitButton, HcaptchaSubmitButton, RecaptchaSubmitButton, SubmitButton, Toast, TurnstileSubmitButton },

  props: {
    buttonClass: {
      type: String,
      required: false,
      default: ''
    },
    calculateSolutionOnMount: {
      type: Boolean,
      required: false,
      default: false
    },
    formValid: {
      type: Boolean,
      required: false,
      default: true
    },
    label: {
      type: String,
      required: true
    },
    captchaAction: {
      type: String,
      required: true
    },
    invisibleCaptcha: {
      type: Boolean,
      required: false,
      default: false
    },
    submitInProgress: {
      type: Boolean,
      required: true
    },
    submittingLabel: {
      type: String,
      required: false
    },
    validationErrors: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  emits: ['submit'],

  data() {
    return {
      showError: false
    }
  },

  computed: {
    buttonComponent() {
      switch (this.agraConfiguration.botProtection.kind) {
      case 'friendly-captcha':
        return FriendlyCaptchaSubmitButton
      case 'hcaptcha':
        return HcaptchaSubmitButton
      case 'recaptcha':
        return RecaptchaSubmitButton
      case 'turnstile':
        return TurnstileSubmitButton
      default:
        return SubmitButton
      }
    },

    buttonListener() {
      // If we are rendering a regular submit button, we need to listen for a "click" event.
      // But if we are rendering any of the bot protection buttons, we need to _ignore_ the "click" event
      // because we only want to know about the "submit-with-token" event that happens once the challenge
      // has been evaluated.
      switch (this.agraConfiguration.botProtection.kind) {
      case 'friendly-captcha':
      case 'hcaptcha':
      case 'recaptcha':
      case 'turnstile':
        return {
          'submit-with-token': this.submitWithToken
        }
      default:
        return {
          click: this.submit
        }
      }
    },

    extraButtonProps() {
      const buttonProps = {}

      if (this.agraConfiguration.botProtection.kind === 'none') {
        buttonProps.class = this.buttonClass
      } else {
        buttonProps.buttonClass = this.buttonClass
        buttonProps.validationErrors = this.validationErrors
      }

      switch (this.agraConfiguration.botProtection.kind) {
      case 'friendly-captcha':
        buttonProps.invisibleCaptcha = this.invisibleCaptcha
        buttonProps.calculateSolutionOnMount = this.calculateSolutionOnMount
        break
      case 'recaptcha':
        buttonProps.recaptchaAction = this.captchaAction
        break
      case 'turnstile':
        buttonProps.turnstileAction = this.captchaAction
        break
      }

      return buttonProps
    }
  },

  methods: {
    errorGettingToken(error) {
      this.showError = true
      console.log('Error getting token:', error)
    },

    recalculateAndResubmit() {
      if (this.agraConfiguration.botProtection.kind === 'recaptcha') {
        // Calling submit() on RecaptchaSubmitButton will recalculate the token as if the button had just been clicked
        this.$refs.button.submit()
      } else {
        throw `recalculateAndResubmit() is not supported for ${this.agraConfiguration.botProtection.kind}`
      }
    },

    submit() {
      this.$emit('submit', {})
    },

    submitWithToken(challengeResponse) {
      this.$emit('submit', challengeResponse)
    }
  }
}
</script>
